'use client';

import { Icon } from '@iconify/react';
import { type TodoProject } from '@prisma/client';
import { Hash, Inbox } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Input } from '../../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { findOrCreateProjectByName } from '../lib/actions/todoActions';

interface ProjectPickerProps {
  projects: TodoProject[];
  selectedProjectId?: string;
  onProjectSelect: (projectId?: string, projectName?: string) => void;
  onProjectCreated: (newProject: TodoProject) => void;
}

function ProjectPickerComponent({
  projects,
  selectedProjectId,
  onProjectSelect,
  onProjectCreated,
}: ProjectPickerProps) {
  const [open, setOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId),
    [projects, selectedProjectId],
  );

  const displayName = selectedProject?.name ?? 'Входящие';

  // Когда поповер закрывается — чистим input "создать проект"
  useEffect(() => {
    if (!open) setNewProjectName('');
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const selectInbox = useCallback(() => {
    onProjectSelect(undefined, 'Входящие');
    close();
  }, [onProjectSelect, close]);

  const selectExisting = useCallback(
    (p: TodoProject) => {
      onProjectSelect(p.id, p.name);
      close();
    },
    [onProjectSelect, close],
  );

  const createOrSelectByName = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed || isSubmitting) return;

      setIsSubmitting(true);
      try {
        const project = await findOrCreateProjectByName(trimmed);

        const alreadyInList = projects.some((p) => p.id === project.id);
        if (!alreadyInList) {
          onProjectCreated(project);
          toast.success(`Проект "${project.name}" создан!`);
        } else {
          toast.success(`Выбран проект "${project.name}"`);
        }

        onProjectSelect(project.id, project.name);
        close();
      } catch (error: unknown) {
        if (error instanceof Error) toast.error(error.message);
        else toast.error('Не удалось создать/найти проект');
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, projects, onProjectCreated, onProjectSelect, close],
  );

  const handleCreateFromInput = useCallback(() => {
    void createOrSelectByName(newProjectName);
  }, [createOrSelectByName, newProjectName]);

  const handleFavorite = useCallback(
    (name: string) => {
      void createOrSelectByName(name);
    },
    [createOrSelectByName],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-300 hover:bg-slate-700 justify-start text-left font-normal"
          aria-label={`Выбран проект: ${displayName}`}
        >
          {selectedProject ? <Hash size={14} className="mr-2" /> : <Inbox size={14} className="mr-2" />}
          <span className="truncate">{displayName}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          {/* Create */}
          <div className="p-2 border-b border-slate-700 flex items-center gap-2">
            <Input
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Создать или выбрать проект..."
              className="h-8 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFromInput();
              }}
              disabled={isSubmitting}
              aria-label="Название нового проекта"
            />
          </div>

          <CommandList>
            <CommandInput placeholder="Найти проект..." />
            <CommandEmpty>Проект не найден.</CommandEmpty>

            <CommandGroup heading="Избранное">
              <CommandItem onSelect={selectInbox}>
                <Inbox className="mr-2 h-4 w-4" /> Входящие
              </CommandItem>
              <CommandItem onSelect={() => handleFavorite('Семья')}>
                <Icon icon="noto-v1:family-man-woman-girl" className="mr-2 h-5 w-5" /> Семья
              </CommandItem>
              <CommandItem onSelect={() => handleFavorite('Работа')}>
                <Icon icon="twemoji:briefcase" className="mr-2 h-5 w-5" /> Работа
              </CommandItem>
            </CommandGroup>

            {projects.length > 0 && (
              <CommandGroup heading="Мои проекты">
                {projects.map((p) => (
                  <CommandItem key={p.id} onSelect={() => selectExisting(p)}>
                    <Hash className="mr-2 h-4 w-4" /> {p.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const ProjectPicker = memo(ProjectPickerComponent);
ProjectPicker.displayName = 'ProjectPicker';
